import { memo, RefObject, useMemo } from "react";
import type { AuctionItem } from "@/types/auction";
import AuctionCard from "./AuctionCard";
import AuctionCardSkeleton from "./AuctionCardSkeleton";
import { useOptimizedPlayerBatch } from "@/hooks/useOptimizedPlayerBatch";

interface AuctionGridProps {
  auctions: AuctionItem[];
  /** All sorted auctions for prefetching (optional - used for prefetch optimization) */
  allAuctions?: AuctionItem[];
  isLoading?: boolean;
  /** True when loading more items (infinite scroll) */
  isLoadingMore?: boolean;
  error?: Error | null;
  hasMore?: boolean;
  totalCount?: number;
  loadMoreRef?: RefObject<HTMLDivElement>;
  showSeller?: boolean;
}

const AuctionGrid = memo(({ 
  auctions, 
  allAuctions,
  isLoading = false, 
  isLoadingMore = false,
  error = null, 
  hasMore = false, 
  totalCount = 0,
  loadMoreRef,
  showSeller = true
}: AuctionGridProps) => {
  // Extract unique seller UUIDs for current visible auctions
  const visibleSellerUuids = useMemo(() => {
    if (!showSeller) return [];
    return [...new Set(auctions.map(a => a.seller))];
  }, [auctions, showSeller]);

  // Extract ALL seller UUIDs for prefetching (from all sorted auctions if provided)
  const allSellerUuids = useMemo(() => {
    if (!showSeller) return [];
    const source = allAuctions || auctions;
    return [...new Set(source.map(a => a.seller))];
  }, [allAuctions, auctions, showSeller]);

  // Use optimized batch loading - only fetches NEW UUIDs and prefetches next batch
  useOptimizedPlayerBatch({
    visibleUuids: visibleSellerUuids,
    allUuids: allSellerUuids,
    visibleCount: auctions.length,
    totalCount: totalCount || auctions.length,
    size: 14,
  });

  // Initial loading - show full skeleton grid
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <AuctionCardSkeleton key={`skeleton-initial-${i}`} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center">
        <p className="text-destructive text-sm">
          Fehler beim Laden der Auktionen. Bitte versuche es später erneut.
        </p>
      </div>
    );
  }

  if (auctions.length === 0) {
    return (
      <div className="rounded-xl border border-border/50 bg-secondary/30 p-6 text-center">
        <p className="text-muted-foreground text-sm">
          Keine aktiven Auktionen in dieser Kategorie gefunden.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
        {auctions.map((auction, index) => (
          <AuctionCard key={`${auction.seller}-${auction.startTime}-${index}`} auction={auction} showSeller={showSeller} />
        ))}
        
        {/* Skeleton placeholders while loading more */}
        {isLoadingMore && Array.from({ length: 4 }).map((_, i) => (
          <AuctionCardSkeleton key={`skeleton-more-${i}`} />
        ))}
      </div>
      
      {hasMore && (
        <div ref={loadMoreRef} className="flex flex-col items-center gap-2 pt-4">
          <p className="text-xs text-muted-foreground">
            {auctions.length} von {totalCount} Auktionen
          </p>
        </div>
      )}
    </div>
  );
});

AuctionGrid.displayName = 'AuctionGrid';

export default AuctionGrid;
